"use client";
import { Menu, Transition } from "@headlessui/react";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { Fragment, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import { Link } from "@tanstack/react-router";
import AnalyticsService from "@/services/analytics";
import { useI18N } from "common/hooks";
import ModalWithAction from "../ModalWithAction";
import RenameList from "./RenameList";
import ImageCropProvider from "@/providers/ImageCropProvider";
import CoverPhotoCrop from "./CoverPhotoCrop";

interface SavedPlace {
  cover_photo: string;
  created_at: string;
  id: number;
  name: string;
  saved_place_details: object;
  total_places: number;
  updated_at: string;
  user_id: number;
  // Add any other fields that are part of your saved place object
}

interface SavePlaceMenuProps {
  onDeleteClick: () => void;
  list: SavedPlace;
}

const SavePlaceMenu = ({ onDeleteClick, list }: SavePlaceMenuProps) => {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openRenameListModal, setOpenRenameListModal] = useState(false);
  const [openUpdateCoverPhotoModal, setOpenUpdateCoverPhotoModal] =
    useState(false);

  const menuItems = [
    {
      label: `${i18n.get("renameSavedList")}`,
      icon: "/images/PencilSimple_Primary500.svg",
      iconDark: "/images/PencilSimple_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler("listSettingsClickedRenameList");
        AnalyticsService.trackMx("listSettingsClickedRenameList", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });
        setOpenRenameListModal(true);
      },
    },
    {
      label: `${i18n.get("updateCoverPhoto")}`,
      icon: "/images/Camera_Primary_500.svg",
      iconDark: "/images/Camera_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler("listSettingsClickedUpdateCoverPhoto");
        AnalyticsService.trackMx("listSettingsClickedUpdateCoverPhoto", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });
        console.log("openUpdateCoverPhotoModal");
        setOpenUpdateCoverPhotoModal(true);
      },
    },
    {
      label: `${i18n.get("delete")}`,
      icon: "/images/Trash_primary_500.svg",
      iconDark: "/images/Trash_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler("listSettingsClickedDeleteList");
        AnalyticsService.trackMx("listSettingsClickedDeleteList", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });

        setIsModalOpen(true);
      },
    },
  ];

  const handleDeleteClick = () => {
    onDeleteClick();
    setIsModalOpen(false);
  };

  const handleRenameListModalClose = () => {
    console.log("Closing Modal handleRenameListModalClose");
    setOpenRenameListModal(false);
  };

  const handleUpdateCoverPhotoModalClose = () => {
    console.log("Closing Modal handleUpdateCoverPhotoModalClose");
    setOpenUpdateCoverPhotoModal(false);
  };

  return (
    <div className="">
      <Menu as="div" className="relative inline-block">
        <Menu.Button
          type="button"
          className="inline-flex items-center"
          aria-label="List options"
        >
          {isDark ? (
            <img src="/images/DotsThree_dark.svg" alt="" aria-hidden="true" />
          ) : (
            <img
              src="/images/DotsThree_Priamry_800.svg"
              alt=""
              aria-hidden="true"
            />
          )}
        </Menu.Button>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Menu.Items
            static
            className="bg-slate-50 dark:bg-primary-900 rounded-2xl shadow border border-violet-100 pt-0 pr-4 pl-4 pb-0 absolute right-0 top-full min-w-[250px] outline-0 focus:outline-0 z-1000"
          >
            <ul className="space-y-1 -mx-4" role="none">
              {menuItems.map((item, index) => (
                <Fragment key={index}>
                  <Menu.Item key={item.label}>
                    {({ active }) => (
                      <Link
                        to={item?.path}
                        className={`flex items-start gap-4 py-2.5 px-4 cursor-pointer first:rounded-t-2xl last:rounded-b-2xl hover:no-underline ${active ? "bg-violet-100 dark:bg-[#37354d] text-primary-800 hover:dark:text-primary-150" : ""}`}
                        role="button"
                        onClick={item?.onclick}
                        tabIndex={0}
                      >
                        <div className="w-[24px] h-[24px]">
                          {isDark ? (
                            <img
                              src={item.iconDark}
                              alt=""
                              className="w-[24px] h-[24px]"
                              aria-hidden="true"
                            />
                          ) : (
                            <img
                              src={item.icon}
                              alt=""
                              className="w-[24px] h-[24px]"
                            />
                          )}
                        </div>
                        <span
                          className="paragraph-medium-regular text-primary-900 dark:text-primary-100"
                        >
                          {item.label}
                        </span>
                      </Link>
                    )}
                  </Menu.Item>
                </Fragment>
              ))}
            </ul>
          </Menu.Items>
        </Transition>
      </Menu>

      <ModalWithAction
        type="error"
        actionButtonType="error"
        isModalOpen={isModalOpen}
        title={`Delete "${list?.name}"?`}
        subtitle={i18n.get("deleteListMessage")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("delete")}
        onClose={() => {
          AnalyticsService.trackGAEventHandler("deleteListClickedCancelDeleteList");
          AnalyticsService.trackMx("deleteListClickedCancelDeleteList", {
            SavedPlaceName: list?.name,
            SavedPlaceId: list?.id,
            SavedPlaceTotalPlaces: list?.total_places,
          });

          setIsModalOpen(false);
        }}
        onClick={handleDeleteClick}
      />

      {openRenameListModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openRenameListModal}
          onClose={() => handleRenameListModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <RenameList
            list={list}
            onClose={() => handleRenameListModalClose()}
          />
        </Modal>
      )}

      {openUpdateCoverPhotoModal && (
        <ImageCropProvider>
          <CoverPhotoCrop
            list={list}
            initiateOpenModal={true}
            onClose={() => handleUpdateCoverPhotoModalClose()}
          />
        </ImageCropProvider>
      )}
    </div>
  );
};

export default SavePlaceMenu;
